import { FC } from 'react';
import classNames from 'classnames';
import { Button, Toggle } from '@carbon/react';
import { Add, Connect, Edit, Grid, TrashCan } from '@carbon/react/icons';
import type { Polygon } from 'geojson';
import { polygonAreaAcres, polygonBboxFeet } from 'utils/geometry-measure';
import { formatNumber } from 'utils/number';
import styles from './polygons-section.module.scss';

interface PolygonsSectionProps {
  polygons: Polygon[];
  /** True while the map is armed for the next polygon. */
  isDrawing: boolean;
  onDraw: () => void;
  onDelete: (index: number) => void;
  /** Index of the polygon whose vertices are being edited, if any. */
  editingIndex: number | null;
  onEditingIndexChange: (index: number | null) => void;
  /**
   * Whether new and moved vertices are pulled onto section lines. Omit the
   * handler to leave the toggle out, for hosts with no survey grid to snap to.
   */
  snapToGrid?: boolean;
  onSnapToGridChange?: (snap: boolean) => void;
  /** Namespaces the toggle id, so two of these on one page do not collide. */
  idPrefix?: string;
  /**
   * The shape the Draw button arms (Area Builder Epic 2 adds circles). Every
   * drawn shape is still listed as a polygon, which is what a circle is saved as.
   */
  shape?: 'polygon' | 'circle';
  /** Off when the host already says how to draw the shape. */
  showDrawingHint?: boolean;
  /** How to draw the armed shape, in place of the polygon default. */
  drawingHint?: string;
  /** Said when nothing is drawn and nothing is armed. */
  emptyHint?: string;
}

/**
 * Whole feet, grouped — "207,433".
 *
 * @param {number} value - Distance in feet.
 * @return {string} The formatted distance.
 */
const feet = (value: number) => formatNumber(value, 0, 0);

/**
 * Acreage, gaining a decimal only when the figure is small enough to need one.
 *
 * @param {number} value - Area in acres.
 * @return {string} The formatted acreage.
 */
const acres = (value: number) => {
  return value < 10 ? formatNumber(value, 0, 1) : formatNumber(value, 0, 0);
};

/**
 * User-drawn: the drawn polygons and the controls that produce them. The
 * drawing itself happens on the catalog map — this arms it, lists what came
 * back with each polygon's size, and lets a polygon be re-edited or dropped.
 *
 * @param {PolygonsSectionProps} props - The component props.
 * @return {JSX.Element} The drawing controls.
 */
export const PolygonsSection: FC<PolygonsSectionProps> = ({
  polygons,
  isDrawing,
  onDraw,
  onDelete,
  editingIndex,
  onEditingIndexChange,
  snapToGrid = false,
  onSnapToGridChange,
  idPrefix = 'draw',
  shape = 'polygon',
  showDrawingHint = true,
  drawingHint,
  emptyHint,
}) => (
  <section className={classNames(styles.section, styles.divided)}>
    <div className={styles.polygonHeader}>
      <h4 className={styles.sectionTitle}>Polygons ({polygons.length})</h4>
      <Button
        kind="ghost"
        size="sm"
        renderIcon={Add}
        disabled={isDrawing || editingIndex !== null}
        onClick={onDraw}
      >
        {polygons.length === 0 ? `Draw ${shape}` : `Add another ${shape}`}
      </Button>
    </div>

    {onSnapToGridChange && (
      <div className={styles.snapToggle}>
        {/*
          Iconography per the mock: Connect (a node snapping onto another) leads
          the toggle, and a faint Grid sits at the row's far end naming what is
          being snapped to.
        */}
        <div className={styles.snapLabel}>
          <Connect size={16} />
          <Toggle
            id={`${idPrefix}-snap`}
            size="sm"
            aria-label="Snap to survey grid"
            labelText=""
            labelA="Snap to survey grid"
            labelB="Snap to survey grid"
            toggled={snapToGrid}
            onToggle={onSnapToGridChange}
          />
          <Grid size={16} className={styles.snapGridGlyph} />
        </div>
        <p className={styles.hint}>
          Drawn and edited vertices near a land-grid section line snap onto it,
          keeping boundaries survey-aligned.
        </p>
      </div>
    )}

    {isDrawing && showDrawingHint && (
      <p className={styles.hint}>
        {drawingHint ??
          'Click on the map to place boundary points, then double-click to finish — the polygon closes automatically.'}
      </p>
    )}

    {!isDrawing && polygons.length === 0 && emptyHint && (
      <p className={styles.hint}>{emptyHint}</p>
    )}

    <div className={styles.polygonList}>
      {polygons.map((polygon, index) => {
        const { widthFt, heightFt } = polygonBboxFeet(polygon);
        const isEditing = editingIndex === index;

        return (
          <div className={styles.polygonRow} key={`polygon-${index}`}>
            <span className={styles.polygonChip}>
              Polygon {index + 1} · {feet(widthFt)} ft × {feet(heightFt)} ft (
              {acres(polygonAreaAcres(polygon))} acres)
            </span>
            <div className={styles.polygonActions}>
              {isEditing ? (
                <Button
                  kind="ghost"
                  size="sm"
                  onClick={() => onEditingIndexChange(null)}
                >
                  Done
                </Button>
              ) : (
                <Button
                  kind="ghost"
                  size="sm"
                  hasIconOnly
                  iconDescription={`Edit polygon ${index + 1}`}
                  renderIcon={Edit}
                  disabled={isDrawing}
                  onClick={() => onEditingIndexChange(index)}
                />
              )}
              <Button
                kind="ghost"
                size="sm"
                hasIconOnly
                iconDescription={`Delete polygon ${index + 1}`}
                renderIcon={TrashCan}
                onClick={() => onDelete(index)}
              />
            </div>
          </div>
        );
      })}
    </div>

    {editingIndex !== null && (
      <p className={styles.hint}>
        Drag the vertices to move them, drag the line handles or click the
        outline to add points, then click Done.
      </p>
    )}
  </section>
);

export default PolygonsSection;
